import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import { Trans } from '@lingui/react/macro';
import { FormProvider, useForm } from 'react-hook-form';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  enrolment: NonNullable<RouterOutputs['enrolment']['byIdWithResourceStatus']>;
};

const DeleteEnrolmentSettingsSchema = z.object({
  text: z.string(),
});

function DeleteEnrolmentModal({ enrolment, isOpen, onClose }: Props) {
  const utils = trpcAdmin.useUtils();
  const deleteEnrolment = trpcAdmin.enrolment.deleteCourseEnrolment.useMutation();
  const methods = useForm<{
    text: string;
  }>({
    resolver: zodResolver(DeleteEnrolmentSettingsSchema),
  });

  const {
    clearErrors,
    reset,
    formState: { errors },
  } = methods;

  const resetFieldsAndClose = () => {
    reset();
    clearErrors();
    onClose();
  };

  return (
    <Modal
      size="2xl"
      isOpen={isOpen}
      onClose={() => {
        onClose();
      }}
    >
      <ModalOverlay />
      <ModalContent
        padding={4}
        minH={'300px'}
        as={'form'}
        onSubmit={methods.handleSubmit(async (data) => {
          await deleteEnrolment.mutateAsync({
            enrolmentId: enrolment.id,
            text: data.text,
          });
          [
            await utils.enrolment.byIdWithResourceStatus.refetch({
              enrolmentId: Number(enrolment.id),
            }),
          ];
          resetFieldsAndClose();
        })}
      >
        <ModalHeader>
          Delete enrolment
          <ModalCloseButton margin={4} />
        </ModalHeader>

        <ModalBody pb={8} h="full">
          <FormProvider {...methods}>
            <VStack>
              <Text>
                <Trans>
                  This action will soft delete this enrolment. It will completely remove it from
                  both the Provider and Student apps and will only be visible to Admins going
                  forward.
                </Trans>
              </Text>
              <FormControl isInvalid={!!errors.text?.message}>
                <FormLabel
                  color="fg.emphasis"
                  fontWeight="normal"
                  fontSize="small"
                  mb={1}
                  display="flex"
                >
                  <Trans>Notes</Trans>
                  <Text color="red">*</Text>
                </FormLabel>
                <Input
                  {...methods.register('text', {
                    required: 'Note is required',
                  })}
                  placeholder="Add any additional notes"
                />
                <FormErrorMessage>{errors.text?.message}</FormErrorMessage>
              </FormControl>
            </VStack>
          </FormProvider>
        </ModalBody>
        <ModalFooter gap={4}>
          <Button type="submit" colorScheme="red">
            <Trans>Delete this enrolment</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default DeleteEnrolmentModal;
